"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTeamChat } from "./TeamChatContext";
import TeamChatView from "./TeamChatView";

const WIDGET_OPEN_KEY = "team_chat_widget_open";

/**
 * 앱 전역 플로팅 채팅 위젯.
 * - 인증된 사용자에게만 표시
 * - /team-chat 페이지에서는 숨김 (Provider 가 실시간 드라이버를 소유하므로 위젯/페이지 동시 존재해도 연결은 1개)
 * - 큰 화면: 우하단 360×520 카드 / 작은 화면: 전체화면 오버레이
 */
export default function TeamChatWidget() {
  const router = useRouter();
  const pathname = usePathname();

  const {
    authed,
    unreadCount,
    realtimeStatus,
    markAllRead,
    lastMessageId,
    refreshAuth,
  } = useTeamChat();

  const [open, setOpen] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
    if (typeof window === "undefined") return;
    const raw = localStorage.getItem(WIDGET_OPEN_KEY);
    if (raw === "1") setOpen(true);
  }, []);

  // 라우트 변화 시 같은 탭 내 로그인/로그아웃 반영
  useEffect(() => {
    refreshAuth();
  }, [pathname, refreshAuth]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(WIDGET_OPEN_KEY, open ? "1" : "0");
  }, [open]);

  useEffect(() => {
    if (open) markAllRead();
  }, [open, markAllRead, lastMessageId]);

  if (!mounted) return null;
  if (!authed) return null;
  if (pathname === "/team-chat") return null;
  // [ERP-MOBILE-20260513-002] 모바일 ERP 에서는 우측 하단 플로팅 채팅 버튼 숨김
  // (모바일 하단 탭에 /mobile/chat 메뉴가 별도로 있음).
  if (pathname === "/mobile" || pathname?.startsWith("/mobile/")) return null;
  // [ERP-POS-FLOATING-WIDGET-HIDE-20260522-001] 오프라인 매장 POS 화면에서는 플로팅 채팅 버튼 숨김
  if (pathname === "/store" || pathname?.startsWith("/store/")) return null;

  const badge =
    unreadCount > 0 ? (unreadCount > 99 ? "99+" : String(unreadCount)) : null;

  function handleExpand() {
    setOpen(false);
    router.push("/team-chat");
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-[150] bottom-5 right-5 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center text-2xl"
          title="팀 채팅"
          aria-label={`팀 채팅 열기${badge ? ` (안 읽음 ${badge})` : ""}`}
        >
          💬
          {badge && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">
              {badge}
            </span>
          )}
          {realtimeStatus === "polling" && (
            <span
              className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-yellow-400 border-2 border-white"
              title="폴링 모드"
            />
          )}
          {realtimeStatus === "offline" && (
            <span
              className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-gray-400 border-2 border-white"
              title="오프라인"
            />
          )}
        </button>
      )}

      {open && (
        <div
          className="fixed z-[150] inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[360px] sm:h-[520px] bg-white sm:rounded-xl shadow-2xl overflow-hidden flex flex-col border border-gray-200"
          role="dialog"
          aria-label="팀 채팅 위젯"
        >
          <TeamChatView
            mode="widget"
            onExpandFullScreen={handleExpand}
            onClose={() => setOpen(false)}
            realtimeStatus={realtimeStatus}
          />
        </div>
      )}
    </>
  );
}
